import { Capacitor } from "@capacitor/core";

export type ClientEnvironment =
  | "development"
  | "local"
  | "preview"
  | "staging"
  | "production";

const LOOPBACK_HOSTNAMES = new Set([
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
  "[::1]",
]);

const PRIVATE_IPV4 = /^(10|192\.168|172\.(1[6-9]|2\d|3[01]))\./;

/**
 * True for hostnames that can only be a developer's own machine or network:
 * loopback, RFC 1918 private IPv4, and mDNS `.local` names. A production
 * build reached through one of these is a dev/test rig, not a deployment —
 * e.g. a sideloaded native build pointed at a LAN backend, or a local prod
 * build opened from another device on the same network.
 */
function isDeveloperHostname(hostname: string): boolean {
  return (
    LOOPBACK_HOSTNAMES.has(hostname) ||
    PRIVATE_IPV4.test(hostname) ||
    hostname.endsWith(".local")
  );
}

/**
 * True when this bundle was compiled by the e2e prod-build harness
 * (`VITE_E2E_TEST_SEAMS=true`, set by the Playwright `webServer` — see
 * `e2e/playwright.config.ts`). Real deploys never set it, so Vite statically
 * folds this to `false` and strips the branch. Do NOT gate it behind
 * `import.meta.env.PROD !== true`: `vite build` runs in production mode, which
 * would dead-strip the flag we rely on.
 */
export function isE2ETestBuild(): boolean {
  return import.meta.env?.VITE_E2E_TEST_SEAMS === "true";
}

/**
 * Pure classifier behind {@link getEnvironment}, split out so the rules are
 * unit-testable (under `bun test` the ambient `import.meta.env.DEV` is always
 * true, which would otherwise collapse every case to `"development"`).
 */
export function classifyEnvironment(input: {
  isDevBuild: boolean;
  isE2EBuild: boolean;
  isNativePlatform: boolean;
  /**
   * Hostname of the backend origin baked into the bundle at build time
   * (`VITE_BASE_URL`), or null when none was baked. Only consulted on the
   * native platform.
   */
  nativeApiHostname: string | null;
  hasWindow: boolean;
  hostname: string;
}): ClientEnvironment {
  if (input.isDevBuild) {
    return "development";
  }

  // No `window`, or a `window` that has no hostname to read: the DOM-less
  // unit-test process, a partial window stub (several suites install one), or
  // any non-browser importer. Nothing is deployed there, so treat it like
  // development rather than classifying a hostname that isn't one.
  //
  // The empty case is load-bearing, not defensive dressing: every rule below
  // this line calls a string method on the hostname, so an absent one used to
  // throw — `undefined is not an object (evaluating 'hostname.endsWith')` —
  // from inside an analytics capture, which surfaced as two unrelated
  // action-sync tests failing in one CI file order. Falling through to the
  // hostname rules would be worse than the crash: an unknown host matches none
  // of them and lands on "production", which is the one answer that turns
  // product analytics ON.
  if (!input.hasWindow || input.hostname === "") {
    return "development";
  }

  if (input.isE2EBuild) {
    return "local";
  }

  // The installed native app serves from `capacitor://localhost`, so its own
  // origin says nothing about which deployment it belongs to — the backend
  // baked in at build time does: a staging-pointed TestFlight build is the
  // staging app, and a build pointed at a developer's machine is local.
  if (input.isNativePlatform && input.nativeApiHostname != null) {
    return classifyDeploymentHostname(input.nativeApiHostname);
  }

  // Native with no baked backend: the WebView is pointed at a live host
  // (`CAP_SERVER_URL`, e.g. a deployed site whose web bundle bakes nothing),
  // which classifies by hostname like the web — except a loopback origin,
  // which on the native platform is the bundled app on a real device and must
  // keep reporting as production.
  if (input.isNativePlatform && LOOPBACK_HOSTNAMES.has(input.hostname)) {
    return "production";
  }

  return classifyDeploymentHostname(input.hostname);
}

function classifyDeploymentHostname(hostname: string): ClientEnvironment {
  // A production build reached through a developer host is not a deployment:
  // the e2e harness (`E2E_SPROCESS_PROD_BUILD`, served on 127.0.0.1:5001), a
  // developer's own `bun run build && bun run start`, or a device on the
  // developer's LAN.
  if (isDeveloperHostname(hostname)) {
    return "local";
  }

  if (hostname.includes("staging")) {
    return "staging";
  }

  // Per-PR previews are served at pr-<n>.preview.s-process.app (see the
  // preview workflow's Traefik router) off their own Neon branch of staging.
  // They're production *builds*, so without this they'd report as production —
  // which mislabels their Sentry events and makes throwaway-environment noise
  // look like a real production signal.
  if (hostname.includes(".preview.")) {
    return "preview";
  }

  return "production";
}

/**
 * Which deployment the running client is part of.
 *
 * Both non-deployment cases exist because a production *build* is not a
 * deployment: `"development"` is a Vite dev server, and `"local"` is a real
 * `vite build` served from a loopback address — the e2e suite's prod-build mode
 * and local prod-build testing. `"local"` used to fall through to
 * `"production"` (nothing about `import.meta.env.PROD` or a `127.0.0.1`
 * hostname says staging), so CI's Playwright crashes on fixture data landed in
 * the production Sentry project tagged `environment: production` — the same
 * mislabeling `"preview"` was added to fix, one rung further from a real
 * deployment.
 */
export function getEnvironment(): ClientEnvironment {
  const hasWindow = typeof window !== "undefined";
  return classifyEnvironment({
    isDevBuild: import.meta.env.DEV,
    isE2EBuild: isE2ETestBuild(),
    isNativePlatform: Capacitor.isNativePlatform(),
    nativeApiHostname: getBakedApiHostname(),
    hasWindow,
    // A real browser always has one; a stubbed `window` may not, and the
    // classifier's contract is a STRING (see its empty-hostname rule).
    hostname: hasWindow ? (window.location?.hostname ?? "") : "",
  });
}

/**
 * Hostname of the backend origin this bundle was compiled against —
 * `VITE_BASE_URL`, which the native release scripts bake in per target env
 * (see `mobile/scripts/targets.ts`) and `trpc.ts` uses to reach the API from
 * the bundled app. Null when unset or not a parseable URL.
 */
function getBakedApiHostname(): string | null {
  const baseUrl = import.meta.env.VITE_BASE_URL;
  if (!baseUrl) {
    return null;
  }
  try {
    return new URL(baseUrl).hostname;
  } catch {
    return null;
  }
}

/**
 * True for builds whose telemetry belongs in the shared Sentry / PostHog
 * projects: deployed web hosts (production, staging, per-PR previews) and the
 * installed native app. False for dev servers and locally served production
 * builds, whose events are fixture data and Playwright clicks —
 * indistinguishable from a funder's real session once ingested.
 *
 * Previews and staging DO report: they're reachable deployments people click
 * through, and their Sentry events stay labeled by environment. What they
 * report is narrower than production — see
 * {@link isProductAnalyticsEnvironment}.
 */
export const DEPLOYED_ENVIRONMENTS = [
  "preview",
  "staging",
  "production",
] as const satisfies readonly ClientEnvironment[];

export function isDeployedEnvironment(): boolean {
  return (DEPLOYED_ENVIRONMENTS as readonly ClientEnvironment[]).includes(
    getEnvironment(),
  );
}

/**
 * Whether this environment's behavioral data belongs in the shared PostHog
 * product analytics: pageviews, autocapture, heatmaps, session replay.
 *
 * Only production does. Non-production deployments still initialize PostHog —
 * they have to, or feature flags evaluate as `undefined` and every flagged
 * surface fails closed, which is why flagged work could not be previewed at
 * all — but they get flags only.
 *
 * The reason is that their traffic isn't product usage. Client events carry no
 * environment property (unlike Sentry and the server-side capture), so staging
 * and preview clicks are indistinguishable from a funder's in any insight that
 * doesn't hand-filter on `$host`: staff exercising fixture data on a Neon
 * branch, read as funder activity — the same mislabeling locally served
 * production builds used to cause.
 */
export function isProductAnalyticsEnvironment(): boolean {
  return environmentCapturesProductAnalytics(getEnvironment());
}

/**
 * Pure rule behind {@link isProductAnalyticsEnvironment}, split out to be
 * unit-testable for the same reason {@link classifyEnvironment} is.
 */
export function environmentCapturesProductAnalytics(
  environment: ClientEnvironment,
): boolean {
  return environment === "production";
}

/**
 * True when running in Vite dev mode.
 * Use this instead of `import.meta.env.DEV` for readability.
 */
export const isDev = import.meta.env.DEV;
